import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getManagedEvent, getChecklist, submitComplianceDoc, publishEvent, updateTier, updateEvent,
  COMPLIANCE_ITEMS, ITEM_LABELS,
  type ManagedEvent, type Checklist, type ComplianceItem, type ManagedTier,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoney, formatTime } from '../../lib/format';
import { EventLineup } from './EventLineup';
import { EventVenueSection } from './EventVenueSection';
import { useEventPaths } from '../../admin/routeBase';
import { usePageChrome } from '../../admin/pageChrome';
import { EventCmsPanel } from '../../admin/cms/EventCmsPanel';
import { AdmissionsPanel } from '../../admin/admissions/AdmissionsPanel';
import './OrganizerPages.css';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Not submitted',
  submitted: 'Under review',
  approved: 'Approved',
  not_required: 'Not required',
};

/**
 * The compliance section when there is no checklist to show.
 *
 * ── IT DOES NOT OFFER TO CREATE ONE ──────────────────────────────────────────────────
 *
 * A checklist is created with the event, in the same transaction. There is no endpoint
 * that initializes one afterwards, and inventing a compliance workflow because a page
 * failed to load would be building a product decision into a bug fix. An event without a
 * checklist is an anomaly worth seeing plainly rather than papering over — and a GET that
 * quietly wrote a row would make it invisible.
 */
function ComplianceUnavailable({ reason }: { reason: 'missing' | 'unavailable' | null }) {
  return (
    <section className="org-card">
      <header className="org-card__head">
        <div>
          <h2 className="org-card__title">Compliance</h2>
        </div>
      </header>
      {reason === 'unavailable'
        ? (
          <p className="org-hint" role="alert">
            The compliance checklist could not be loaded just now. Everything else on this
            page is up to date; reload to try again.
          </p>
        )
        : (
          <p className="org-hint">
            No compliance checklist exists for this event yet. Checklists are created with
            the event, so this one predates that or was created outside the normal flow.
            The event cannot be published until compliance is complete.
          </p>
        )}
    </section>
  );
}

/**
 * Compliance, from the organizer's side.
 *
 * Read-mostly by design: an organizer may SUBMIT documentation, which moves an
 * item to "under review", but only an admin can approve. That split is the whole
 * point of the gate, so the UI states it rather than offering an approve control
 * the engine would reject.
 */
function ComplianceSection({
  eventId, checklist, onChanged,
}: {
  eventId: string;
  checklist: Checklist;
  onChanged: (next: Checklist) => void;
}) {
  const [editing, setEditing] = useState<ComplianceItem | null>(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(item: ComplianceItem) {
    if (!url.trim()) return setError('Paste a link to the document.');
    setBusy(true);
    setError(null);
    try {
      const next = await submitComplianceDoc(eventId, item, url.trim());
      onChanged(next);
      setEditing(null);
      setUrl('');
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not submit that document.');
    } finally {
      setBusy(false);
    }
  }

  const outstanding = COMPLIANCE_ITEMS.filter((i) => {
    const s = checklist.items[i]?.status;
    return s !== 'approved' && s !== 'not_required';
  }).length;

  return (
    <section className="org-card">
      <header className="org-card__head">
        <div>
          <h2 className="org-card__title">Compliance</h2>
          <p className="org-hint">
            Eight items. Voleto reviews and approves each one, you submit the paperwork.
          </p>
        </div>
        {checklist.overallCompliant ? (
          <span className="org-ready">✓ All clear</span>
        ) : (
          <span className="org-blocked">
            <span className="mono">{outstanding}</span> outstanding
          </span>
        )}
      </header>

      {error && <p className="org-error" role="alert">{error}</p>}

      <ul className="comp-list">
        {COMPLIANCE_ITEMS.map((item) => {
          const entry = checklist.items[item] ?? { status: 'pending', docUrl: null };
          const done = entry.status === 'approved' || entry.status === 'not_required';
          return (
            <li key={item} className={`comp-item comp-item--${entry.status}`}>
              <div className="comp-item__main">
                <span className="comp-item__name">{ITEM_LABELS[item]}</span>
                <span className={`org-pill org-pill--${entry.status}`}>
                  {STATUS_LABEL[entry.status]}
                </span>
              </div>

              {entry.docUrl && (
                <a className="comp-item__doc" href={entry.docUrl}
                   target="_blank" rel="noreferrer noopener">
                  View submitted document
                </a>
              )}

              {!done && (
                editing === item ? (
                  <div className="comp-item__form">
                    <input
                      className="field__input" type="url" value={url} autoFocus
                      placeholder="https://…/document.pdf"
                      onChange={(e) => setUrl(e.target.value)}
                    />
                    <button type="button" className="button button--primary"
                      disabled={busy} onClick={() => submit(item)}>
                      {busy ? 'Submitting…' : 'Submit'}
                    </button>
                    <button type="button" className="button button--ghost"
                      onClick={() => { setEditing(null); setUrl(''); }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button type="button" className="button button--secondary comp-item__action"
                    onClick={() => { setEditing(item); setUrl(entry.docUrl ?? ''); }}>
                    {entry.docUrl ? 'Replace document' : 'Submit document'}
                  </button>
                )
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Inline tier editing for a draft. Capacity below sold is refused by the engine. */
function TierEditor({
  eventId, tiers, onChanged,
}: {
  eventId: string;
  tiers: ManagedTier[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', price: '', quantity: '', variationRef: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function begin(tier: ManagedTier) {
    setEditing(tier.id);
    setForm({
      name: tier.name,
      price: String(tier.priceCents / 100),
      quantity: String(tier.quantityTotal),
      variationRef: tier.variationRef ?? '',
    });
    setError(null);
  }

  async function save(tier: ManagedTier) {
    setBusy(true);
    setError(null);
    try {
      await updateTier(eventId, tier.id, {
        name: form.name.trim(),
        priceCents: Math.round(Number(form.price) * 100),
        quantityTotal: Number.parseInt(form.quantity, 10),
        // Null rather than empty string, so clearing it and never setting it are the same
        // state. Voleto never interprets this value; it exists to be read by a human
        // reconciling against another system.
        variationRef: form.variationRef.trim() || null,
      });
      setEditing(null);
      onChanged();
    } catch (err: unknown) {
      // The engine refuses capacity below quantity_sold; say why in plain terms.
      const code = err instanceof ApiError ? err.message : '';
      setError(
        code === 'quantity_below_sold'
          ? `Capacity cannot go below the ${tier.quantitySold} already sold.`
          : code || 'Could not save that tier.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="org-card">
      <header className="org-card__head">
        <h2 className="org-card__title">Ticket tiers</h2>
      </header>

      {error && <p className="org-error" role="alert">{error}</p>}

      {tiers.length === 0 && (
        <p className="org-hint">No tiers yet, buyers have nothing to purchase.</p>
      )}

      <ul className="wiz-tiers">
        {tiers.map((t) => (
          <li key={t.id} className="wiz-tier">
            {editing === t.id ? (
              <>
                <input className="field__input" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <input className="field__input mono" type="number" step="0.01" min="0" value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
                <input className="field__input mono" type="number" min={t.quantitySold || 1} value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />
                {/* Variation #. Somebody else's identifier, stored so a promoter
                    reconciling against another system does not keep that mapping in a
                    spreadsheet. Voleto never reads it. */}
                <input className="field__input mono" value={form.variationRef}
                  placeholder="Variation #" aria-label="External variation reference"
                  onChange={(e) => setForm((f) => ({ ...f, variationRef: e.target.value }))} />
                <span className="wiz-tier__edit-actions">
                  <button type="button" className="button button--primary" disabled={busy}
                    onClick={() => save(t)}>Save</button>
                  <button type="button" className="button button--ghost"
                    onClick={() => setEditing(null)}>Cancel</button>
                </span>
              </>
            ) : (
              <>
                <div>
                  <span className="wiz-tier__name">{t.name}</span>
                  {t.description && <span className="wiz-tier__desc">{t.description}</span>}
                </div>
                <span className="wiz-tier__price mono">{formatMoney(t.priceCents)}</span>
                {t.variationRef && (
                  <span className="wiz-tier__variation mono" title="External variation reference">
                    #{t.variationRef}
                  </span>
                )}
                <span className="wiz-tier__qty mono">
                  {t.quantitySold} / {t.quantityTotal} sold
                </span>
                <button type="button" className="button button--ghost wiz-tier__remove"
                  onClick={() => begin(t)}>Edit</button>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Edit everything about an event, in draft or after publishing.
 *
 * Published events were previously uneditable from the UI: the only way in was a link
 * shown when isDraft, so a typo in a live listing could not be corrected without going
 * through support. The engine has allowed these edits all along; nothing but the button
 * was missing.
 *
 * Publishing is NOT one of these fields. Status changes go through the publish gate,
 * which checks compliance, and updateEvent refuses a status in its body precisely so this
 * form cannot become a way around that.
 *
 * Saves the whole form rather than diffing it. The payload is small, the engine only
 * writes the keys it is given, and a partial save that silently dropped a field the user
 * had cleared would be worse than sending everything.
 */
function DetailsEditor({ event, onSaved }: { event: ManagedEvent; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Dates arrive as ISO timestamps and <input type="date"> wants YYYY-MM-DD.
  const day = (v: string | null) => (v ? v.slice(0, 10) : '');
  const clock = (v: string | null) => (v ? v.slice(0, 5) : '');

  const [f, setF] = useState({
    title: event.title ?? '',
    description: event.description ?? '',
    venueName: event.venueName ?? '',
    venueAddress: event.venueAddress ?? '',
    venueCity: event.venueCity ?? '',
    venueState: event.venueState ?? '',
    eventDate: day(event.eventDate),
    eventTime: clock(event.eventTime),
    doorsTime: clock(event.doorsTime),
    eventEndDate: day(event.eventEndDate),
    eventEndTime: clock(event.eventEndTime),
    genre: event.genre ?? '',
    category: event.category ?? '',
    ageRestriction: event.ageRestriction ?? '',
    expectedAttendance: event.expectedAttendance != null ? String(event.expectedAttendance) : '',
    featuredImageUrl: event.featuredImageUrl ?? '',
    featuredVideoUrl: event.featuredVideoUrl ?? '',
    featuredVideoWebmUrl: event.featuredVideoWebmUrl ?? '',
  });

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  // Empty means "unset", which has to reach the engine as null rather than an empty
  // string, or a cleared field would save as blank text and read back as present.
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());

  async function save() {
    if (!f.title.trim()) { setError('A title is required.'); return; }
    setSaving(true);
    setError(null);
    setFlash(null);
    try {
      await updateEvent(event.id, {
        title: f.title.trim(),
        description: orNull(f.description),
        venueName: orNull(f.venueName),
        venueAddress: orNull(f.venueAddress),
        venueCity: orNull(f.venueCity),
        venueState: orNull(f.venueState),
        eventDate: orNull(f.eventDate),
        eventTime: orNull(f.eventTime),
        doorsTime: orNull(f.doorsTime),
        eventEndDate: orNull(f.eventEndDate),
        eventEndTime: orNull(f.eventEndTime),
        genre: orNull(f.genre),
        category: orNull(f.category),
        ageRestriction: orNull(f.ageRestriction),
        expectedAttendance: f.expectedAttendance.trim() === ''
          ? null : Number(f.expectedAttendance),
        featuredImageUrl: orNull(f.featuredImageUrl),
        featuredVideoUrl: orNull(f.featuredVideoUrl),
        featuredVideoWebmUrl: orNull(f.featuredVideoWebmUrl),
      });
      setFlash('Saved.');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the changes.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="org-card">
      <div className="org-card__head">
        <h2 className="org-card__title">Event details</h2>
        <button type="button" className="button button--secondary"
          onClick={() => { setOpen((v) => !v); setFlash(null); }}>
          {open ? 'Close' : 'Edit event'}
        </button>
      </div>

      {!open && (
        <p className="org-hint">
          Title, dates, venue, description, genre and media. Editable whether the event is
          a draft or already live.
        </p>
      )}

      {flash && <p className="org-flash">{flash}</p>}
      {error && <p className="adm-error" role="alert">{error}</p>}

      {open && (
        <div className="org-card__body">
          <div className="wiz-grid">
            <label className="field wiz-field--wide">
              <span className="field__label">Title *</span>
              <input className="field__input" type="text" value={f.title} onChange={set('title')} />
            </label>

            <label className="field wiz-field--wide">
              <span className="field__label">About this event</span>
              <textarea className="field__input wiz-textarea" rows={4}
                value={f.description} onChange={set('description')} />
            </label>

            <label className="field">
              <span className="field__label">Start date</span>
              <input className="field__input" type="date" value={f.eventDate} onChange={set('eventDate')} />
            </label>
            <label className="field">
              <span className="field__label">Start time</span>
              <input className="field__input" type="time" value={f.eventTime} onChange={set('eventTime')} />
            </label>
            <label className="field">
              <span className="field__label">End date (optional)</span>
              <input className="field__input" type="date" value={f.eventEndDate} onChange={set('eventEndDate')} />
            </label>
            <label className="field">
              <span className="field__label">End time (optional)</span>
              <input className="field__input" type="time" value={f.eventEndTime} onChange={set('eventEndTime')} />
            </label>
            <label className="field">
              <span className="field__label">Doors</span>
              <input className="field__input" type="time" value={f.doorsTime} onChange={set('doorsTime')} />
            </label>

            <label className="field">
              <span className="field__label">Venue name</span>
              <input className="field__input" type="text" value={f.venueName} onChange={set('venueName')} />
            </label>
            <label className="field wiz-field--wide">
              <span className="field__label">Address</span>
              <input className="field__input" type="text" value={f.venueAddress} onChange={set('venueAddress')} />
            </label>
            <label className="field">
              <span className="field__label">City</span>
              <input className="field__input" type="text" value={f.venueCity} onChange={set('venueCity')} />
            </label>
            <label className="field">
              <span className="field__label">State</span>
              <input className="field__input" type="text" value={f.venueState} onChange={set('venueState')} />
            </label>

            <label className="field">
              <span className="field__label">Genre</span>
              <input className="field__input" type="text" value={f.genre} onChange={set('genre')} />
            </label>
            <label className="field">
              <span className="field__label">Category</span>
              <input className="field__input" type="text" value={f.category} onChange={set('category')} />
            </label>
            <label className="field">
              <span className="field__label">Age restriction</span>
              <input className="field__input" type="text" value={f.ageRestriction}
                placeholder="18+" onChange={set('ageRestriction')} />
            </label>
            <label className="field">
              <span className="field__label">Expected attendance</span>
              <input className="field__input" type="number" min="0" value={f.expectedAttendance}
                onChange={set('expectedAttendance')} />
            </label>

            <label className="field wiz-field--wide">
              <span className="field__label">Header image URL *</span>
              <input className="field__input" type="url" value={f.featuredImageUrl}
                placeholder="https://…/hero.jpg" onChange={set('featuredImageUrl')} />
            </label>
            <label className="field wiz-field--wide">
              <span className="field__label">Hero video URL, MP4 (optional)</span>
              <input className="field__input" type="url" value={f.featuredVideoUrl}
                placeholder="https://…/hero.mp4" onChange={set('featuredVideoUrl')} />
            </label>
            <label className="field wiz-field--wide">
              <span className="field__label">Hero video URL, WebM (optional)</span>
              <input className="field__input" type="url" value={f.featuredVideoWebmUrl}
                placeholder="https://…/hero.webm" onChange={set('featuredVideoWebmUrl')} />
            </label>
          </div>

          <p className="org-hint">
            Either video format works on its own. The image is always shown first and stays
            visible if a video is slow, missing, or the visitor has reduced motion on.
          </p>

          <div className="wiz-actions">
            <button type="button" className="button button--primary" disabled={saving}
              onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function EventManage() {
  const { eventId } = useParams<{ eventId: string }>();
  const paths = useEventPaths();

  const [event, setEvent] = useState<ManagedEvent | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [checklistError, setChecklistError] = useState<'missing' | 'unavailable' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // ── ONE AUXILIARY RECORD CANNOT TAKE THIS PAGE DOWN ───────────────────────
  //
  // These were loaded with Promise.all, which rejects as soon as either does. An event
  // whose compliance checklist row is missing therefore rendered NOTHING: no title, no
  // tiers, no lineup, no page layout — a blank screen reading "checklist_not_found",
  // for a record that has nothing to do with most of this screen.
  //
  // The event is what this page is about, so failing to load it is still a page-level
  // error. Everything else is a section, and a section that cannot load says so where it
  // sits while the rest of the screen stays usable.
  const load = useCallback(async () => {
    if (!eventId) return;
    const [eventResult, checklistResult] = await Promise.allSettled([
      getManagedEvent(eventId),
      getChecklist(eventId),
    ]);

    if (eventResult.status === 'fulfilled') {
      setEvent(eventResult.value);
      setError(null);
    } else {
      const err = eventResult.reason;
      setError(err instanceof ApiError ? err.message : 'Could not load this event.');
    }

    if (checklistResult.status === 'fulfilled') {
      setChecklist(checklistResult.value);
      setChecklistError(null);
    } else {
      const err = checklistResult.reason;
      setChecklist(null);
      // A 404 is the specific, expected case: the event has no checklist row. Anything
      // else is a genuine failure and is reported as one, because "no checklist exists"
      // and "we could not find out" are different facts and lead to different actions.
      setChecklistError(err instanceof ApiError && err.status === 404
        ? 'missing'
        : 'unavailable');
    }

    setLoading(false);
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  // The breadcrumb's last label is the event's TITLE, taken from the event this screen
  // has already loaded. §8 forbids a second fetch purely to label a crumb, and splitting
  // the URL would put a uuid there instead of a name.
  usePageChrome({
    breadcrumb: [
      { label: 'Admin', to: '/admin' },
      { label: 'Events', to: paths.list },
      { label: event?.title ?? 'Event' },
    ],
  }, [paths.list, event?.title]);

  async function doPublish() {
    if (!eventId) return;
    setPublishing(true);
    setPublishError(null);
    try {
      await publishEvent(eventId);
      await load();
    } catch (err: unknown) {
      const code = err instanceof ApiError ? err.message : '';
      setPublishError(
        code === 'not_compliant'
          ? 'Compliance is not complete. Every item must be approved by Voleto before this event can go on sale.'
          : code === 'not_in_draft'
            ? 'This event is no longer a draft.'
            : code || 'Could not publish this event.',
      );
    } finally {
      setPublishing(false);
    }
  }

  if (loading) return <p className="org-status">Loading event…</p>;
  if (error || !event) {
    return (
      <div className="org-status org-error">
        <p role="alert">{error ?? 'Event not found.'}</p>
        <Link to={paths.list} className="button button--secondary">Back to your events</Link>
      </div>
    );
  }

  const sold = event.tiers.reduce((n, t) => n + t.quantitySold, 0);
  const capacity = event.tiers.reduce((n, t) => n + t.quantityTotal, 0);
  const revenue = event.tiers.reduce((n, t) => n + t.quantitySold * t.priceCents, 0);
  const isDraft = event.status === 'draft';

  return (
    <div className="org-page">
      <header className="org-header">
        <div>
          <nav className="org-breadcrumb">
            <Link to={paths.list}>← Events</Link>
          </nav>
          <h1 className="org-title display">{event.title}</h1>
          <p className="org-lede">
            <span className={`org-status-pill org-status--${event.status}`}>{event.status}</span>
            {' · '}
            {formatDateShort(event.eventDate)}
            {event.eventTime ? ` · ${formatTime(event.eventTime)}` : ''}
            {event.venueName ? ` · ${event.venueName}` : ''}
          </p>
        </div>
      </header>

      <DetailsEditor event={event} onSaved={load} />

      {/* --- Publish gate ---------------------------------------------- */}
      {isDraft && (
        <section className={`org-publish${event.canPublish ? ' org-publish--ready' : ''}`}>
          <div>
            <h2 className="org-publish__title">
              {event.canPublish ? 'Ready to publish' : 'Not ready to publish'}
            </h2>
            <p className="org-publish__body">
              {event.canPublish
                ? 'Compliance is cleared. Publishing puts tickets on sale immediately and notifies followers of the confirmed lineup.'
                : 'Publishing is blocked until Voleto has approved every compliance item below. This is enforced at the database level, the event cannot go on sale before then.'}
            </p>
            {publishError && <p className="org-error" role="alert">{publishError}</p>}
          </div>
          <button
            type="button"
            className="button button--primary"
            disabled={!event.canPublish || publishing}
            title={event.canPublish ? undefined : 'Compliance incomplete'}
            onClick={doPublish}
          >
            {publishing ? 'Publishing…' : 'Publish event'}
          </button>
        </section>
      )}

      {/* --- Venue ------------------------------------------------------ */}
      <EventVenueSection event={event} onChanged={() => void load()} />

      {/* --- Sales ------------------------------------------------------ */}
      <section className="org-card">
        <header className="org-card__head">
          <h2 className="org-card__title">Sales</h2>
        </header>
        <div className="org-metrics">
          <div className="org-metric">
            <span className="org-metric__label">Tickets sold</span>
            <span className="org-metric__value mono">{sold}</span>
          </div>
          <div className="org-metric">
            <span className="org-metric__label">Capacity</span>
            <span className="org-metric__value mono">{capacity}</span>
          </div>
          <div className="org-metric">
            <span className="org-metric__label">Remaining</span>
            <span className="org-metric__value mono">{Math.max(capacity - sold, 0)}</span>
          </div>
          <div className="org-metric">
            <span className="org-metric__label">Gross</span>
            <span className="org-metric__value mono org-metric__value--gold">
              {formatMoney(revenue)}
            </span>
          </div>
        </div>

        {event.tiers.length > 0 && (
          <table className="org-table">
            <thead>
              <tr>
                <th scope="col">Tier</th>
                <th scope="col">Price</th>
                <th scope="col">Sold</th>
                <th scope="col">Remaining</th>
                <th scope="col">Gross</th>
              </tr>
            </thead>
            <tbody>
              {event.tiers.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="mono">{formatMoney(t.priceCents)}</td>
                  <td className="mono">{t.quantitySold}</td>
                  <td className="mono">{t.remaining}</td>
                  <td className="mono">{formatMoney(t.quantitySold * t.priceCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Admin only, and it renders nothing in the Organizer portal. The CMS console
          is an admin surface; an organizer has nowhere to be sent. */}
      <EventCmsPanel eventId={event.id} />

      {/* §37/§87. How this event admits people, beside the event it belongs to. Business
          behaviour, deliberately not in the CMS editor: the CMS decides where the
          admission surface sits, this decides what it does. */}
      <AdmissionsPanel eventId={event.id} />

      <EventLineup eventId={event.id} />

      <TierEditor eventId={event.id} tiers={event.tiers} onChanged={load} />

      {checklist
        ? (
          <ComplianceSection
            eventId={event.id}
            checklist={checklist}
            onChanged={(next) => { setChecklist(next); void load(); }}
          />
        )
        : <ComplianceUnavailable reason={checklistError} />}
    </div>
  );
}
